import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { THEME, flexxState, awardXP } from "./state";
import { CAPSTONE_QUESTIONS, CAPSTONE_THRESHOLD, SIM_TRADE_MIN, SIM_WINRATE_MIN, SIM_AVG_RR_MIN, COMMUNITY } from "./capstoneData";
import { PHASES } from "./masteryData";
import KnowledgeRadar from "./KnowledgeRadar";
import Portal from "./Portal";
import NextGenCTA from "./NextGenCTA";
import { FlexxCertificate, openCertificatePrintView } from "./FlexxCertificate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("flexx_access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Compute capstone readiness from current state
function getCapstoneState() {
  const gates = flexxState.gates();
  const gatesPassed = PHASES.every((p) => gates[p.id]?.passed);
  const fc = flexxState.flashcards();
  const flashcardsReviewed = Object.values(fc).length >= 10; // at least 10 cards seeded/reviewed
  const journal = flexxState.journal();
  const decided = journal.filter((e) => e.result !== "Break Even");
  const winRate = decided.length ? Math.round((journal.filter((e) => e.result === "Win").length / decided.length) * 100) : 0;
  const wins = journal.filter((e) => e.result === "Win");
  const avgRR = wins.length ? +(wins.reduce((a, e) => a + (e.rr || 0), 0) / wins.length).toFixed(2) : 0;
  const simDone = journal.length >= SIM_TRADE_MIN && winRate >= SIM_WINRATE_MIN && avgRR >= SIM_AVG_RR_MIN;
  const saved = flexxState.capstone();
  return { gatesPassed, flashcardsReviewed, simDone, winRate, avgRR, tradeCount: journal.length, ...saved };
}

// Capstone scorecard 5 axes
function computeScorecard(st) {
  const ruleClarity = st.plan.text.length > 800 ? 95 : st.plan.text.length > 400 ? 80 : st.plan.text.length > 0 ? 55 : 0;
  const riskDiscipline = st.simDone ? 90 : Math.min(90, Math.round(st.winRate + 10));
  const psychologyAwareness = st.plan.text.toLowerCase().includes("psychology") || st.plan.text.toLowerCase().includes("emotion")
    ? 85 : st.plan.text.length > 0 ? 65 : 0;
  const performanceMetrics = Math.min(100, Math.round((st.quiz.score || 0)));
  const planSustainability = Math.min(100, Math.round(((st.plan.text.match(/\n|\./g) || []).length * 5 + st.plan.text.length / 30)));
  return { ruleClarity, riskDiscipline, psychologyAwareness, performanceMetrics, planSustainability };
}

export default function MasteryCapstone() {
  const [st, setSt] = useState(getCapstoneState());
  const [stage, setStage] = useState("hub"); // hub | quiz | plan | review | cert

  const refresh = () => setSt(getCapstoneState());

  if (!st.gatesPassed) {
    return <Locked />;
  }

  const allReady = st.quiz.done && st.plan.text.length >= 300 && st.simDone && st.flashcardsReviewed;

  return (
    <div data-testid="flexx-capstone" className="flexx-fade-in">
      {st.certificateIssued ? (
        <CertificateView st={st} />
      ) : (
        <>
          <div className="flexx-card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>MASTERY CAPSTONE</div>
            <h2 style={{ color: THEME.text, fontSize: 22, margin: "6px 0 8px" }}>Final Exit — Phase 4</h2>
            <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              Complete all four requirements below to unlock the FLEXX Certified Trader credential.
            </p>
          </div>

          <div style={{ display: "grid", gap: 12 }}>
            <Requirement
              id="cap-req-quiz"
              done={st.quiz.done}
              title={`1. Comprehensive 30-question exam (${CAPSTONE_THRESHOLD}% to pass)`}
              subtitle={st.quiz.done ? `Passed — ${st.quiz.score}%` : "Covers all four phases."}
              action="Start Exam"
              onAction={() => setStage("quiz")}
            />
            <Requirement
              id="cap-req-plan"
              done={st.plan.text.length >= 300}
              title="2. Submit a written Trading Plan (300+ words)"
              subtitle={st.plan.text.length >= 300 ? `Submitted — ${st.plan.text.length} chars` : "Edge, entry rules, risk rules, psychology guardrails."}
              action={st.plan.text.length >= 300 ? "Edit" : "Write Plan"}
              onAction={() => setStage("plan")}
            />
            <Requirement
              id="cap-req-sim"
              done={st.simDone}
              title={`3. 10-trade Sim Challenge (winrate >${SIM_WINRATE_MIN}%, avg R:R >${SIM_AVG_RR_MIN})`}
              subtitle={st.simDone
                ? `Cleared — ${st.tradeCount} trades, ${st.winRate}% wins, 1:${st.avgRR}`
                : `Logged ${st.tradeCount}/${SIM_TRADE_MIN} trades · ${st.winRate}% wins · avg R:R 1:${st.avgRR}`}
              action="Open Journal"
              onAction={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
            <Requirement
              id="cap-req-flashcards"
              done={st.flashcardsReviewed}
              title="4. Review all flashcard decks at least once"
              subtitle={st.flashcardsReviewed ? "Coverage confirmed." : "Seed + review the 4 decks to count."}
              action="Open Flashcards"
              onAction={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
          </div>

          {allReady && (
            <button
              data-testid="capstone-issue-certificate-btn"
              className="flexx-btn-primary"
              style={{ width: "100%", marginTop: 18, padding: "14px 20px", fontSize: 15 }}
              onClick={() => setStage("review")}
            >
              Review scorecard & issue certificate →
            </button>
          )}

          {stage === "quiz" && <CapstoneQuizModal onClose={() => { setStage("hub"); refresh(); }} />}
          {stage === "plan" && <CapstoneplanModal initial={st.plan.text} onClose={() => { setStage("hub"); refresh(); }} />}
          {stage === "review" && <CapstoneReview st={st} onClose={() => { setStage("hub"); refresh(); }} onIssue={() => { refresh(); setStage("cert"); }} />}
        </>
      )}
    </div>
  );
}

function Locked() {
  return (
    <div data-testid="capstone-locked" className="flexx-card flexx-fade-in" style={{ textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 48, marginBottom: 10 }}>🔒</div>
      <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 4 }}>CAPSTONE LOCKED</div>
      <h2 style={{ color: THEME.text, fontSize: 20, margin: "6px 0 10px" }}>Clear all 4 Mastery Gates first</h2>
      <p style={{ color: THEME.mute, fontSize: 13, lineHeight: 1.6, maxWidth: 420, margin: "0 auto" }}>
        The capstone is the exit exam of the 12-Week Mastery Protocol. Finish every phase gate — then this door unlocks.
      </p>
    </div>
  );
}

function Requirement({ id, done, title, subtitle, action, onAction }) {
  return (
    <div data-testid={id} className="flexx-card" style={{ padding: 16, borderColor: done ? `${THEME.green}80` : THEME.border, background: done ? `${THEME.green}10` : THEME.surface, display: "flex", alignItems: "center", gap: 12 }}>
      <div style={{ width: 34, height: 34, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${done ? THEME.green : THEME.border}`, color: done ? THEME.green : THEME.mute, fontWeight: 800 }}>
        {done ? "✓" : "·"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: THEME.text, fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div style={{ color: THEME.mute, fontSize: 12, marginTop: 2 }}>{subtitle}</div>
      </div>
      <button className="flexx-btn-ghost" style={{ fontSize: 12 }} onClick={onAction} data-testid={`${id}-action-btn`}>{action}</button>
    </div>
  );
}

function CapstoneQuizModal({ onClose }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [selected, setSelected] = useState(null);
  const [locked, setLocked] = useState(false);
  const [result, setResult] = useState(null);
  const q = CAPSTONE_QUESTIONS[idx];

  const choose = (i) => {
    if (locked) return;
    setSelected(i);
    setLocked(true);
    const next = { ...answers, [idx]: i };
    setAnswers(next);
    setTimeout(() => {
      if (idx + 1 >= CAPSTONE_QUESTIONS.length) {
        const correct = CAPSTONE_QUESTIONS.reduce((a, qq, qi) => a + (next[qi] === qq.correct ? 1 : 0), 0);
        const pct = Math.round((correct / CAPSTONE_QUESTIONS.length) * 100);
        const passed = pct >= CAPSTONE_THRESHOLD;
        flexxState.saveCapstone({ quiz: { done: passed, score: pct } });
        if (passed) awardXP(300, "Capstone exam passed");
        setResult({ pct, passed, correct });
      } else {
        setIdx(idx + 1);
        setSelected(null);
        setLocked(false);
      }
    }, 550);
  };

  if (result) {
    return (
      <Portal>
      <div data-testid="capstone-quiz-result" style={overlay}>
        <div className="flexx-slide-up" style={container}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>EXAM RESULT</div>
          <h2 style={{ color: THEME.text, fontSize: 24, margin: "6px 0 8px" }}>{result.pct}% · {result.correct}/{CAPSTONE_QUESTIONS.length}</h2>
          <div style={{ color: result.passed ? THEME.green : THEME.red, fontWeight: 700, marginBottom: 14 }}>
            {result.passed ? `✓ Passed — ${CAPSTONE_THRESHOLD}% threshold cleared` : `Below ${CAPSTONE_THRESHOLD}% — retake allowed`}
          </div>
          <button data-testid="capstone-quiz-close-btn" className="flexx-btn-primary" style={{ width: "100%" }} onClick={onClose}>Close</button>
        </div>
      </div>
      </Portal>
    );
  }

  const pct = Math.round(((idx + (locked ? 1 : 0)) / CAPSTONE_QUESTIONS.length) * 100);

  return (
    <Portal>
    <div data-testid="capstone-quiz-modal" style={overlay}>
      <div className="flexx-slide-up" style={container}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>CAPSTONE EXAM</div>
            <div style={{ color: THEME.text, fontSize: 16, fontWeight: 700 }}>Question {idx + 1} / {CAPSTONE_QUESTIONS.length}</div>
          </div>
          <button aria-label="Close" onClick={onClose} data-testid="capstone-quiz-x" style={{ background: "transparent", border: "none", color: THEME.mute, fontSize: 22, cursor: "pointer" }}>×</button>
        </div>
        <div className="flexx-progress-bar" style={{ margin: "10px 0 14px" }}><span style={{ width: `${pct}%` }} /></div>
        <p style={{ color: THEME.text, fontSize: 15, lineHeight: 1.55, marginBottom: 14 }}>{q.q}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {q.choices.map((c, i) => {
            const isSel = selected === i;
            const showCorrect = locked && i === q.correct;
            const showWrong = locked && isSel && i !== q.correct;
            const cls = ["flexx-quiz-option", isSel && !locked ? "selected" : "", showCorrect ? "correct" : "", showWrong ? "wrong flexx-shake" : ""].join(" ");
            return (
              <button key={i} data-testid={`capstone-option-${idx}-${i}`} disabled={locked} className={cls} onClick={() => choose(i)}>
                <span style={{ width: 22, height: 22, borderRadius: "50%", border: `1px solid ${THEME.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: THEME.mute }}>
                  {String.fromCharCode(65 + i)}
                </span>
                <span>{c}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
    </Portal>
  );
}

function CapstoneplanModal({ initial, onClose }) {
  const [text, setText] = useState(initial || "");
  const wc = text.trim().length;
  const minChars = 300;

  const submit = () => {
    flexxState.saveCapstone({ plan: { text, submittedAt: Date.now() } });
    if (text.length >= 300) awardXP(150, "Trading plan submitted");
    onClose();
  };

  return (
    <Portal>
    <div data-testid="capstone-plan-modal" style={overlay}>
      <div className="flexx-slide-up" style={{ ...container, maxWidth: 640 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 4 }}>TRADING PLAN</div>
        <h2 style={{ color: THEME.text, fontSize: 20, margin: "6px 0 10px" }}>Write your trading plan</h2>
        <p style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.6, marginBottom: 12 }}>
          Cover: your edge · entry rules · risk rules · psychology guardrails. Minimum {minChars} characters.
        </p>
        <textarea
          data-testid="capstone-plan-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={14}
          style={{ width: "100%", padding: 14, borderRadius: 10, background: "#13161c", border: `1px solid ${THEME.border}`, color: THEME.text, fontSize: 13, lineHeight: 1.6, outline: "none", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }}
          placeholder="My edge: … My entry rules: … Risk: … Psychology: …"
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, fontSize: 12, color: wc >= minChars ? THEME.green : THEME.mute }}>
          <span data-testid="capstone-plan-charcount">{wc} / {minChars} chars minimum</span>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="flexx-btn-ghost" onClick={onClose}>Cancel</button>
            <button data-testid="capstone-plan-submit-btn" className="flexx-btn-primary" disabled={wc < minChars} onClick={submit}>Submit Plan</button>
          </div>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function CapstoneReview({ st, onClose, onIssue }) {
  const scorecard = useMemo(() => computeScorecard(st), [st]);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState(null);
  const axes = {
    marketStructure: scorecard.ruleClarity,
    ta: scorecard.performanceMetrics,
    risk: scorecard.riskDiscipline,
    psychology: scorecard.psychologyAwareness,
    strategy: scorecard.planSustainability,
  };

  const issue = async () => {
    setIssuing(true);
    setError(null);
    try {
      // Ensure latest state is on server before minting the cert (backend re-validates from flexx_state)
      try {
        await axios.put(`${API}/flexx/state`, { data: flexxState.snapshot() }, { headers: authHeaders() });
      } catch {}
      const res = await axios.post(`${API}/flexx/certificate/issue`, null, { headers: authHeaders() });
      flexxState.saveCapstone({
        certificateIssued: true,
        issuedAt: res.data.issued_at,
        certId: res.data.cert_id,
        certSignature: res.data.signature,
        verifyUrl: res.data.verify_url,
      });
      flexxState.addAchievement("flexx_certified");
      awardXP(500, "FLEXX Certified Trader");
      onIssue();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object") {
        const failed = Object.entries(detail.checks || {}).filter(([, v]) => !v).map(([k]) => k);
        setError(`Backend validation failed: ${failed.join(", ") || "unknown"}`);
      } else {
        setError(typeof detail === "string" ? detail : "Unable to issue certificate. Try again.");
      }
    } finally {
      setIssuing(false);
    }
  };

  return (
    <Portal>
    <div data-testid="capstone-review-modal" style={overlay}>
      <div className="flexx-slide-up" style={{ ...container, maxWidth: 600 }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace" }}>CAPSTONE SCORECARD</div>
        <h2 style={{ color: THEME.text, fontSize: 22, margin: "6px 0 12px" }}>Your Trader Profile</h2>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
          <KnowledgeRadar scores={axes} size={280} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 }}>
          <ScoreRow label="Rule Clarity" value={scorecard.ruleClarity} />
          <ScoreRow label="Risk Discipline" value={scorecard.riskDiscipline} />
          <ScoreRow label="Psychology Awareness" value={scorecard.psychologyAwareness} />
          <ScoreRow label="Performance Metrics" value={scorecard.performanceMetrics} />
          <ScoreRow label="Plan Sustainability" value={scorecard.planSustainability} />
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="flexx-btn-ghost" style={{ flex: 1 }} onClick={onClose} disabled={issuing}>Close</button>
          <button
            data-testid="capstone-confirm-issue-btn"
            className="flexx-btn-primary"
            style={{ flex: 1 }}
            onClick={issue}
            disabled={issuing}
          >
            {issuing ? "Issuing…" : "Issue my Certificate 🎓"}
          </button>
        </div>
        {error && (
          <div data-testid="capstone-issue-error" style={{ marginTop: 10, color: THEME.red, fontSize: 12, textAlign: "center" }}>{error}</div>
        )}
      </div>
    </div>
    </Portal>
  );
}

function ScoreRow({ label, value }) {
  return (
    <div style={{ background: "#13161c", border: `1px solid ${THEME.border}`, borderRadius: 10, padding: 10 }}>
      <div style={{ color: THEME.mute, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ color: value >= 80 ? THEME.green : value >= 60 ? THEME.gold : THEME.red, fontSize: 22, fontWeight: 800 }}>{value}</div>
    </div>
  );
}

function CertificateView({ st }) {
  const profile = flexxState.profile();
  const issuedDate = st.issuedAt ? new Date(st.issuedAt).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "—";
  const [showConfetti, setShowConfetti] = useState(true);
  const [copied, setCopied] = useState(false);
  const certId = st.certId;
  // Prefer the backend-provided full verify URL (built from FRONTEND_URL) so the
  // link always points at the production domain, not the current preview host.
  const verifyUrl = st.verifyUrl || (certId ? `${window.location.origin}/verify/${certId}` : null);

  useEffect(() => {
    const t = setTimeout(() => setShowConfetti(false), 6000);
    return () => clearTimeout(t);
  }, []);

  const copyVerify = async () => {
    if (!verifyUrl) return;
    try {
      await navigator.clipboard.writeText(verifyUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };

  return (
    <div data-testid="capstone-certificate-view" className="flexx-fade-in">
      {showConfetti && <Confetti />}
      <FlexxCertificate
        studentName={profile.name || "FLEXX Trader"}
        courseName="Core Principles of Financial Markets & Trading Strategy"
        courseSubtitle="A foundational component of the FLEXX ACADEMY Elite Trading Series"
        completionDate={issuedDate}
        verifyCode={certId || ""}
        tier="foundation"
        onDownload={() => openCertificatePrintView({
          studentName: profile.name || "FLEXX Trader", tier: "foundation",
          courseName: "Core Principles of Financial Markets & Trading Strategy",
          courseSubtitle: "A foundational component of the FLEXX ACADEMY Elite Trading Series",
          completionDate: issuedDate, verifyCode: certId || "",
        })} />

      {verifyUrl && (
        <div className="flexx-card" style={{ marginTop: 16, textAlign: "center" }}>
          <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>SHAREABLE VERIFICATION</div>
          <div style={{ color: THEME.mute, fontSize: 12, lineHeight: 1.55, marginBottom: 10 }}>
            This certificate is backed by a cryptographic signature on the FLEXX Academy server.
            Anyone with the link below can instantly verify its authenticity.
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
            <input
              readOnly
              data-testid="certificate-verify-url"
              value={verifyUrl}
              onFocus={(e) => e.target.select()}
              style={{ flex: "1 1 320px", padding: "9px 12px", borderRadius: 10, background: "#13161c", border: `1px solid ${THEME.border}`, color: THEME.text, fontSize: 12, fontFamily: "monospace", outline: "none", minWidth: 200 }}
            />
            <button
              data-testid="certificate-copy-btn"
              className={copied ? "flexx-btn-ghost" : "flexx-btn-primary"}
              onClick={copyVerify}
            >
              {copied ? "✓ Copied" : "Copy Link"}
            </button>
            <a
              data-testid="certificate-open-verify-link"
              href={verifyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flexx-btn-ghost"
              style={{ textDecoration: "none" }}
            >
              Open verify page →
            </a>
          </div>
        </div>
      )}

      <div className="flexx-card" style={{ marginTop: 16, textAlign: "center" }}>
        <div style={{ fontSize: 10, letterSpacing: 2, color: THEME.gold, fontFamily: "monospace", marginBottom: 6 }}>JOIN THE FLEXX CERTIFIED COMMUNITY</div>
        <div style={{ color: THEME.text, fontSize: 14, lineHeight: 1.6, marginBottom: 14 }}>
          You've earned a seat at the table. Connect with other FLEXX Certified Traders.
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
          <a data-testid="community-discord-link" className="flexx-btn-primary" href={COMMUNITY.discord} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            Join Discord →
          </a>
          <a data-testid="community-telegram-link" className="flexx-btn-ghost" href={COMMUNITY.telegram} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
            Join Telegram →
          </a>
        </div>
      </div>

      {/* High-intent moment: student just earned the FLEXX Certified credential */}
      <NextGenCTA variant="celebration" studentName={profile?.name || ""} />
    </div>
  );
}

function Confetti() {
  const pieces = useRef(
    Array.from({ length: 80 }).map((_, i) => ({
      left: Math.random() * 100,
      delay: Math.random() * 0.8,
      duration: 2.4 + Math.random() * 1.6,
      color: [THEME.gold, THEME.teal, THEME.green, "#C9A227", "#4A90D9"][i % 5],
      rot: Math.random() * 360,
    }))
  );
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 9980 }} aria-hidden="true">
      {pieces.current.map((p, i) => (
        <span
          key={i}
          className="flexx-confetti-piece"
          style={{
            left: `${p.left}%`,
            background: p.color,
            animationDelay: `${p.delay}s`,
            animationDuration: `${p.duration}s`,
            transform: `rotate(${p.rot}deg)`,
          }}
        />
      ))}
    </div>
  );
}

const overlay = {
  position: "fixed", inset: 0, zIndex: 9800,
  background: "rgba(0,0,0,0.9)",
  display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
};
const container = {
  background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 18, padding: 26,
  maxWidth: 540, width: "100%", maxHeight: "92vh", overflowY: "auto",
};
